import React, { useEffect, useState } from "react";
import {
	useNavigate,
	useSearchParams,
	Link,
	useParams,
} from "react-router-dom";
import { Radio, Space, Button, notification, Tag } from "antd";
import {
	AntdRadioButton,
	FilterActions,
	Filters,
	RadioOptions,
} from "./styles";
import Main from "../../../../layout/main/Main";
import { COLORS, Typo } from "../../../../layout/theme";
import DataTable from "../../../../common/data-table/DataTable";
import { useAuth } from "../../../../../contexts/authContext";
import ConversationDrawer from "./ConversationDrawer";
import {
	get_agent_orders_header_info,
	get_agent_orders_page,
	update_agent_orders,
} from "../../../../../api/endpoints/agents";
import { CONSTANTS } from "../../../../../utils/constants";
import moment from "moment";
import OrderConversationDrawer from "./OrderConversationDrawer";
import PrimaryButton from "../../../../common/antd-buttons/PrimaryButton";
import DefaultButton from "../../../../common/antd-buttons/DefaultButton";

export default function AgentOrders() {
	const { id: clientId } = useParams();
	const navigate = useNavigate();
	const { userdata } = useAuth();
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");

	const [data, setData] = useState(null);
	const [state, setState] = useState(CONSTANTS.ORDER_STATE_PENDING);
	const [headerInfo, setHeaderInfo] = useState(null);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [currentConversationId, setCurrentConversationId] = useState(null);
	const [selectedOrder, setSelectedOrder] = useState(null);
	const [pageSize, setPageSize] = useState(CONSTANTS.PAGE_MEDIUM_SIZE);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const columns = [
		{
			title: () => <div style={{ color: COLORS.White }}>#TRF</div>,
			dataIndex: "orderNumId",
			width: 80,
			render: (value) => (
				<Typo type="primary" fontWeight={600} fontSize={14}>
					{value}
				</Typo>
			),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Punto de Venta</div>,
			dataIndex: "pointOfSaleName",
			width: 200,
			render: (value) => (
				<Typo type="secondary" fontWeight={500} fontSize={14}>
					{value}
				</Typo>
			),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Cuit</div>,
			dataIndex: "pointOfSaleCuit",
			width: 120,
			render: (value) => (
				<Typo type="secondary" fontWeight={500} fontSize={14}>
					{value}
				</Typo>
			),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Laboratorio</div>,
			dataIndex: "laboratoryName",
			render: (value) => (
				<Typo type="primary" fontWeight={500} fontSize={14}>
					{value}
				</Typo>
			),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Drogueria</div>,
			dataIndex: "distributorName",
			render: (value) => (
				<Typo type="primary" fontWeight={500} fontSize={14}>
					{value}
				</Typo>
			),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Fecha</div>,
			dataIndex: "created",
			width: 110,
			render: (value) => (
				<Typo type="secondary" fontWeight={400} fontSize={13}>
					{moment(value).format("DD-MM-YYYY")}
				</Typo>
			),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Items</div>,
			dataIndex: "totalItems",
			width: 80,
			align: "right",
			render: (value) => (
				<Typo type="primary" fontWeight={500} fontSize={14}>
					{value}
				</Typo>
			),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Total</div>,
			dataIndex: "total",
			width: 120,
			align: "right",
			render: (value) => (
				<Typo type="secondary" fontWeight={500} fontSize={14}>
					${value?.toFixed(2)}
				</Typo>
			),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Estado</div>,
			dataIndex: "state",
			width: 120,
			render: (v, r) => (
				<Tag color={CONSTANTS.AGENT_ORDERS[v?.toLowerCase()]?.color || "blue"}>
					{CONSTANTS.AGENT_ORDERS[v?.toLowerCase()]?.label || ""}
				</Tag>
			),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Conversación</div>,
			dataIndex: "conversationId",
			width: 160,
			render: (value, record) => (
				<Button
					type="link"
					size="small"
					onClick={() => handleOpenConversation(value, record)}
					disabled={!value}
				>
					Ver detalle
				</Button>
			),
		},
	];

	const fetch_data_orders = async (page, search) => {
		const res = await get_agent_orders_page(search, state, page, pageSize);
		setData(res);
	};

	useEffect(() => {
		fetch_data_orders(1, clientId);
	}, [state, clientId]);

	const fetch_orders_header = async (search) => {
		const res = await get_agent_orders_header_info(search);
		setHeaderInfo(res);
	};

	useEffect(() => {
		fetch_orders_header(clientId);
	}, [state, clientId]);

	// ==========================
	// Manejo de selección masiva
	// ==========================
	const rowSelection = {
		selectedRowKeys,
		onChange: (newSelectedRowKeys) => {
			setSelectedRowKeys(newSelectedRowKeys);
		},
	};

	const handleBulkUpdate = async (newState) => {
		if (!selectedRowKeys.length) {
			notification.warning({
				message: "Sin órdenes seleccionadas",
				description: "Seleccioná al menos una orden para actualizar su estado.",
			});
			return;
		}

		try {
			await update_agent_orders({
				orderIds: selectedRowKeys,
				state: newState,
				clientId,
			});

			notification.success({
				message: "Estados actualizados",
				description: `Se actualizaron ${
					selectedRowKeys.length
				} órdenes al estado ${
					CONSTANTS.AGENT_ORDERS[newState.toLowerCase()]?.label
				}.`,
			});

			setSelectedRowKeys([]);
			fetch_data_orders(1, clientId);
			fetch_orders_header(clientId);
		} catch (error) {
			console.error(error);
			notification.error({
				message: "Error al actualizar",
				description: "No se pudieron actualizar los estados de las órdenes.",
			});
		}
	};

	// ==========================
	// Drawer de conversación
	// ==========================
	const handleOpenConversation = (conversationId, order) => {
		if (!conversationId) return;
		setCurrentConversationId(conversationId);
		setSelectedOrder(order);
		setDrawerOpen(true);
	};

	const handleCloseDrawer = () => {
		setDrawerOpen(false);
		setCurrentConversationId(null);
		setSelectedOrder(null);
	};

	return (
		<Main>
			<Main.Breadcrumb>
				<Main.BreadcrumbItem>
					{uri ? (
						<Link to={"/" + uri}>Configuración General</Link>
					) : (
						<Link to="/">Home</Link>
					)}
				</Main.BreadcrumbItem>
				<Main.BreadcrumbSeparator />
				<Main.ActiveBreadcrumbItem>Pedidos Agente</Main.ActiveBreadcrumbItem>
			</Main.Breadcrumb>

			<Main.Body>
				<Filters>
					<RadioOptions>
						<Radio.Group
							value={state}
							onChange={(e) => setState(e.target.value)}
						>
							<AntdRadioButton value={CONSTANTS.ORDER_STATE_PENDING}>
								Pendientes ({headerInfo?.totalPending ?? 0})
							</AntdRadioButton>
							<AntdRadioButton value={CONSTANTS.ORDER_STATE_ACTIVE}>
								Activas ({headerInfo?.totalActive ?? 0})
							</AntdRadioButton>
							<AntdRadioButton value={CONSTANTS.ORDER_STATE_CANCELED}>
								Canceladas ({headerInfo?.totalCanceled ?? 0})
							</AntdRadioButton>
						</Radio.Group>
					</RadioOptions>
					<FilterActions>
						{selectedRowKeys.length > 0 && (
							<Typo type="secondary" fontSize={12}>
								{selectedRowKeys.length} orden(es) seleccionada(s)
							</Typo>
						)}
						<PrimaryButton
							type="primary"
							onClick={() => handleBulkUpdate(CONSTANTS.ORDER_STATE_ACTIVE)}
							disabled={!selectedRowKeys.length}
							style={{ height: 32, fontWeight: 400 }}
						>
							Marcar como ACTIVAS
						</PrimaryButton>
						<DefaultButton
							danger
							onClick={() => handleBulkUpdate(CONSTANTS.ORDER_STATE_CANCELED)}
							disabled={!selectedRowKeys.length}
							style={{ height: 32, fontWeight: 400 }}
						>
							Marcar como CANCELADAS
						</DefaultButton>
					</FilterActions>
				</Filters>
				<DataTable
					data={data}
					fetch={fetch_data_orders}
					columns={columns}
					rowKey="id"
					rowSelection={rowSelection}
					containerMarginTop={"0px"}
				/>

				<OrderConversationDrawer
					open={drawerOpen}
					onClose={handleCloseDrawer}
					conversationId={currentConversationId}
					order={selectedOrder}
				/>
			</Main.Body>
		</Main>
	);
}
